"use client";

import React, { useEffect, useState } from 'react';
import { InstagramPost } from '@/services/queries/get-instagram-posts';
import { getInstagramPostsAction } from '@/actions/get-instagram-posts';
import SliderClientComponent from './SliderClientComponent';
import ImageComponent from './ImageComponent';

export interface InstagramPostsSliderComponentProps {
  config: {
    numberOfPosts: number;
    resourceGroupId: string;
    applicationId?: string;
    height?: string;
    width?: string;
    margin?: string;
    padding?: string;
    rounded?: string;
    size?: string;
    disableAnimation?: boolean;
    disableCaptions?: boolean;
    textColor?: string;
    bgColor?: string;
    borderColor?: string;
    iconColor?: string;
    iconSize?: string;
    postRounded?: string;
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    objectPosition?: 'top' | 'center' | 'bottom' | 'left' | 'right';
    aspectRatio?: string;
    sliderConfig: {
      navigation: boolean;
      autoplay: {
        delay: number;
        disableOnInteraction: boolean;
        pauseOnMouseEnter: boolean;
      };
      spaceBetween: string;
      centeredSlides: boolean;
      speed: number;
      effect: string;
      slidesPerView?: number | 'auto';
    };
  };
  applicationId?: string;
}

const InstagramPostsSliderComponent: React.FC<InstagramPostsSliderComponentProps> = ({
  config,
  applicationId
}) => {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log('🎯 Instagram Slider Props:', {
    applicationId,
    config: config ? {
      resourceGroupId: config.resourceGroupId,
      numberOfPosts: config.numberOfPosts,
      height: config.height,
      width: config.width
    } : null
  });

  const finalApplicationId = applicationId || config?.applicationId || '';

  useEffect(() => {
    const fetchPosts = async () => {
      if (!finalApplicationId || !config.resourceGroupId) {
        console.warn('Missing required parameters for Instagram posts:', {
          applicationId: finalApplicationId,
          resourceGroupId: config.resourceGroupId
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('📸 Fetching Instagram posts with params:', {
          applicationId: finalApplicationId,
          resourceGroupId: config.resourceGroupId,
          numberOfPosts: config.numberOfPosts
        });
        
        const result = await getInstagramPostsAction(
          finalApplicationId,
          config.resourceGroupId,
          config.numberOfPosts
        );

        console.log('📸 Instagram posts response:', result);

        if (result.success && result.data?.data) {
          console.log('📸 Instagram posts fetched successfully:', {
            requested: config.numberOfPosts,
            received: result.data.data.length,
            posts: result.data.data.map(p => ({ id: p.id, caption: p.caption?.substring(0, 50) + '...' }))
          });
          setPosts(result.data.data);
        } else {
          console.error('Failed to fetch Instagram posts:', result);
          setError(result.error || 'No Instagram posts found');
        }
      } catch (err) {
        console.error('Error fetching Instagram posts:', err);
        setError('Failed to load Instagram posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [finalApplicationId, config.resourceGroupId, config.numberOfPosts]);

  // Convert posts to slider items (ImageComponent format)
  const sliderItems = posts.map((post, index) => {
    const isVideo = post.mediaType === 'VIDEO';
    const imageUrl = isVideo ? post.thumbnailUrl : post.mediaUrl;
    
    console.log('🎬 Processing post:', {
      id: post.id,
      mediaType: post.mediaType,
      isVideo,
      mediaUrl: post.mediaUrl,
      thumbnailUrl: post.thumbnailUrl,
      finalImageUrl: imageUrl
    });
    
    const imageConfig = {
      height: "100%",
      width: "100%",
      objectFit: config.objectFit || "cover",
      objectPosition: config.objectPosition || "center",
      rounded: config.postRounded || "lg",
      aspectRatio: config.aspectRatio || "auto",
      url: imageUrl || '',
      altText: post.caption || `Instagram post ${index + 1}`
    };

    return <ImageComponent key={post.id} config={imageConfig} />;
  });

  console.log('🎠 Slider items created:', {
    postsCount: posts.length,
    sliderItemsCount: sliderItems.length,
    firstPost: posts[0] ? {
      id: posts[0].id,
      mediaType: posts[0].mediaType,
      hasMediaUrl: !!posts[0].mediaUrl,
      hasThumbnailUrl: !!posts[0].thumbnailUrl
    } : null
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading Instagram posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600">No Instagram posts available</p>
        </div>
      </div>
    );
  }

  // Create slider config exactly like regular SliderComponent
  const sliderConfig = {
    cmsData: { applicationId: finalApplicationId } as any,
    app: { id: 'instagram-slider' } as any,
    items: sliderItems,
    config: {
      navigation: config.sliderConfig.navigation,
      slidesPerView: config.sliderConfig.slidesPerView ?? "auto",
      spaceBetween: config.sliderConfig.spaceBetween ?? "1px",
      centeredSlides: config.sliderConfig.centeredSlides ?? false,
      speed: config.sliderConfig.speed ?? 500,
      effect: config.sliderConfig.effect ?? "slide",
      autoplay: config.sliderConfig.autoplay.delay > 0 ? config.sliderConfig.autoplay : undefined
    }
  };

  return (
    <div 
      className="w-full"
      style={{
        height: config.height || 'auto',
        width: config.width || '100%',
      } as React.CSSProperties}
    >
      <SliderClientComponent config={sliderConfig} items={sliderItems} />
    </div>
  );
};

export default InstagramPostsSliderComponent;